import {
  CogIcon,
  CoinsIcon,
  GlobeIcon,
  GraduationCapIcon,
  HomeIcon,
  PickaxeIcon,
  RocketIcon,
  WrenchIcon,
  ZapIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  context: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Geothermal Energy Fundamentals: The Foundation',
    subtitle: 'The foundation your students need to understand everything else',
    context:
      'Your first introduction to geothermal energy can be transformative or overwhelming. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'Introduction to Geothermal Energy',
      "Earth's Internal Heat and Geothermal Gradients",
      'Heat Transfer and Thermal Energy',
      'Types of Geothermal Resources',
      'Renewable Energy Principles',
      'Benefits, Limitations, and Applications',
      'Geothermal Energy in Modern Energy Systems',
      'Economic and Environmental Considerations',
      'Global Geothermal Development',
      'Classroom Applications and Conceptual Examples',
      'Review & Teacher Guidance',
    ],
    result:
      'Your students understand geothermal energy as a practical renewable discipline that connects Earth science, energy engineering, and sustainability—not an impossible list of terms to memorize.',
    icon: WrenchIcon,
  },
  {
    number: '2',
    title: 'Geothermal Geology, Resources & Exploration',
    subtitle: 'Everything your students need to know about geothermal resources and geological foundations',
    context:
      'This is where many students struggle to build the geology foundation. This volume gives you the materials to help them truly understand geothermal resources.',
    items: [
      "Earth's Geological Structure and Tectonic Activity",
      'Volcanic and Non-Volcanic Geothermal Systems',
      'Hydrothermal Reservoirs and Geological Formations',
      'Rock Permeability and Geothermal Fluids',
      'Geological Exploration Techniques',
      'Geophysical and Geochemical Exploration Methods',
      'Resource Assessment and Reservoir Characteristics',
      'Exploration Uncertainty and Development Potential',
      'Temperature Gradients and Resource Classification',
      'Practical Examples of Geothermal Resources',
      'Troubleshooting Reasoning',
      'Classroom Applications',
      '15 worked geothermal geology and resource examples',
    ],
    result: 'Your students learn the geological principles that underpin all geothermal energy development.',
    icon: CogIcon,
  },
  {
    number: '3',
    title: 'Geothermal Drilling & Well Engineering',
    subtitle: 'Explore drilling and well systems in geothermal environments',
    context:
      'Geothermal drilling is critical but complex. This volume gives you everything needed so your students grasp well engineering clearly.',
    items: [
      'Fundamentals of Geothermal Drilling',
      'Drilling Equipment and Technologies',
      'Well Construction and Completion',
      'Production and Reinjection Wells',
      'Well Integrity and Monitoring',
      'High-Temperature Drilling Challenges',
      'Thermal Stress and Material Selection',
      'Well Performance and Maintenance',
      'Wellbore Stability and Fluid Management',
      'Common Drilling Problems and Solutions',
      'Troubleshooting and Optimization',
      'Classroom Applications and Integrated Cases',
      '15 worked geothermal drilling and well engineering examples',
    ],
    result: 'Your students understand how geothermal wells are designed and operated in demanding thermal environments.',
    icon: PickaxeIcon,
  },
  {
    number: '4',
    title: 'Geothermal Power Generation Technologies',
    subtitle: 'Explore how geothermal energy is converted to electricity',
    context: 'Power generation is diverse and important. This volume makes these systems understandable.',
    items: [
      'Fundamentals of Geothermal Electricity Generation',
      'Dry Steam Power Plants',
      'Flash Steam Power Plants',
      'Binary Cycle Power Plants',
      'Turbines, Generators, and Heat Exchangers',
      'Thermodynamic Principles and Efficiency',
      'Power Plant Operation and Performance',
      'Heat Recovery and Optimization Concepts',
      'Electricity Reliability and Grid Integration',
      'Comparison of Generation Technologies',
      'Environmental Considerations and Emissions',
      'Classroom Applications',
      '12 worked geothermal power generation examples',
    ],
    result:
      'Your students can explain geothermal power plants and understand when each technology is most effective.',
    icon: ZapIcon,
  },
  {
    number: '5',
    title: 'Geothermal Heating & Direct-Use Applications',
    subtitle: 'Understand thermal applications beyond electricity generation',
    context:
      'Direct-use applications are practical and important. This volume makes these concepts accessible and understandable.',
    items: [
      'Direct Geothermal Heating Fundamentals',
      'District Heating Systems',
      'Ground-Source Heat Pumps',
      'Residential and Commercial Heating',
      'Greenhouse Heating and Agriculture',
      'Aquaculture and Industrial Applications',
      'Thermal Energy Storage Systems',
      'Thermal Management and Energy Efficiency',
      'Hybrid Heating Systems',
      'Application Selection and Design Concepts',
      'Environmental and Economic Considerations',
      'Classroom Applications',
      '12 worked geothermal heating and direct-use examples',
    ],
    result:
      'Your students understand how geothermal heat is used in heating, agriculture, and industrial applications.',
    icon: HomeIcon,
  },
  {
    number: '6',
    title: 'Environmental Impacts, Safety & Sustainability',
    subtitle: 'Integrates environmental responsibility and sustainability into geothermal development',
    context:
      'Environmental considerations are fundamental to modern geothermal. This volume develops sustainable professionals.',
    items: [
      'Environmental Impacts of Geothermal Development',
      'Greenhouse Gas Emissions and Carbon Benefits',
      'Water Management and Geothermal Fluids',
      'Induced Seismicity and Geological Risks',
      'Reservoir Monitoring and Reinjection',
      'Environmental Protection and Mitigation Strategies',
      'Long-Term Sustainability and Operational Safety',
      'Land Use and Ecosystem Considerations',
      'Water Quality and Thermal Pollution',
      'Regulatory Compliance and Environmental Standards',
      'Classroom Safety and Environmental Scenarios',
      '15 practical environmental and sustainability case studies',
    ],
    result:
      'Your students understand that geothermal development sustainability is built on environmental awareness, risk management, and continuous improvement.',
    icon: GlobeIcon,
  },
  {
    number: '7',
    title: 'Geothermal Energy Economics & Global Markets',
    subtitle: 'Explores the business and policy landscape of geothermal energy',
    context: 'Economics and markets drive real-world development. This volume develops practical understanding.',
    items: [
      'Geothermal Project Development Fundamentals',
      'Exploration and Drilling Investments',
      'Capital and Operating Costs',
      'Financial Feasibility Analysis and Project Risk',
      'Electricity and Heating Markets',
      'Government Policies and Energy Incentives',
      'Energy Pricing and Market Mechanisms',
      'International Geothermal Development',
      'Energy Transition and Grid Integration',
      'Cost Comparison with Other Renewables',
      'Investment Decision-Making',
      'Classroom Applications',
      '12 worked geothermal economics and market examples',
    ],
    result:
      'Your students understand how geothermal projects are financed and how policy shapes geothermal development.',
    icon: CoinsIcon,
  },
  {
    number: '8',
    title: 'Advanced Geothermal Technologies, Innovation & Future Developments',
    subtitle: 'Explore cutting-edge and emerging geothermal innovations',
    context:
      'Advanced technologies represent the future of geothermal. This volume develops forward-thinking perspectives.',
    items: [
      'Enhanced Geothermal Systems (EGS) Fundamentals',
      'Advanced Closed-Loop Geothermal Technologies',
      'Superhot Rock Geothermal Research',
      'Advanced Drilling and Reservoir Engineering',
      'Underground Thermal Energy Storage',
      'Hybrid Geothermal and Renewable Energy Systems',
      'Geothermal and Hydrogen Energy Coupling',
      'Emerging Innovations and Research Directions',
      'Technical Challenges and Solutions',
      'Global Innovation Initiatives',
      'Future Geothermal Development Potential',
      'Classroom Applications',
      '15 practical advanced geothermal and innovation case studies',
    ],
    result:
      'Your students understand emerging geothermal technologies and their potential to expand renewable energy capacity.',
    icon: RocketIcon,
  },
  {
    number: '9',
    title: 'Teaching Geothermal Energy: Lesson Planning, Classroom Activities & Educational Strategies',
    subtitle: 'Your complete guide as a geothermal energy education professional',
    context:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year of geothermal energy teaching.',
    items: [
      'Fundamentals of Geothermal Energy Education',
      'Lesson Planning and Learning Objectives',
      'Strategies for Explaining Complex Geothermal Concepts',
      'Classroom Activities and Instructional Approaches',
      'Teaching Geothermal Geology and Resources',
      'Teaching Drilling and Well Engineering',
      'Teaching Power Generation and Heating',
      'Teaching Environmental and Economic Considerations',
      'Teaching Advanced Technologies and Innovation',
      'Student Engagement and Critical Thinking',
      'Case-Based Learning and Problem-Based Learning',
      'Assessment Strategies and Student Evaluation',
      'Differentiated Instruction and Learning Adaptations',
      'Teaching Sequences and Capstone Projects',
      '10 complete integrated geothermal energy projects',
    ],
    result:
      'You\'ll never wonder "what do I teach today?" again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
